import "@/styles/SimpleKeyboardOverrides.css";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useCommoditiesQuery } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import {
  LAYER_MOVE_MODE,
  type LayerMoveMode,
} from "@/pages/sy/models/LayerMoveMode";
import { CustomKeypad } from "@/pages/sy/components/CustomKeypad";
import { CommoditySelectorForOperation } from "../components/CommoditySelectorForOperation";
import { WeightInputForOperation } from "../components/WeightInputForOperation";
import { ErrorDetailsModal } from "./ErrorDetailsModal";
import {
  normalizeCraneMagnitudeInput,
  parseCraneMagnitude,
} from "../utils/formatCraneMagnitude";

interface ChargeLayerModalForOperationProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    commodityId: number | null;
    weight: number;
    layerNumber?: number;
    setAsActive?: boolean;
    placementMode?: LayerMoveMode | null;
  }) => Promise<void>;
  isLoading: boolean;
  title: string;
  chargeId: number | null;
  selectedLayer?: ChargeLayer | null;
  layers: ChargeLayer[];
  maxLayerNumber: number;
  onDelete?: () => void;
}

export const ChargeLayerModalForOperation = ({
  open,
  onClose,
  onSave,
  isLoading,
  title,
  selectedLayer,
  layers,
  maxLayerNumber,
  onDelete,
}: ChargeLayerModalForOperationProps) => {
  const { data: commodities = [] } = useCommoditiesQuery();
  const [commodityId, setCommodityId] = useState<number | null>(null);
  const [weight, setWeight] = useState<string>("");
  const [layerNumber, setLayerNumber] = useState<number>(1);
  const [setAsActive, setSetAsActive] = useState<boolean>(true);
  const [showCommodityError, setShowCommodityError] = useState<boolean>(false);
  const [isAddToExistingLayer, setIsAddToExistingLayer] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  // Use isLoading from parent (covers all mutation states: save, delete, etc.)

  const enabledCommodities = commodities.filter((c) => c.IsEnable);
  const isEditMode = !!selectedLayer;

  const otherLayers = layers.filter(
    (layer) => layer.ChargeLayerId !== selectedLayer?.ChargeLayerId,
  );
  const isAloneInLayer =
    isEditMode &&
    !otherLayers.some(
      (layer) => layer.LayerNumber === selectedLayer.LayerNumber,
    );
  const maxSelectableLayerNumber = maxLayerNumber + (isAloneInLayer ? 0 : 1);
  const targetLayerMaterials = otherLayers.filter(
    (layer) => layer.LayerNumber === layerNumber,
  );
  const isPlacementRequired =
    targetLayerMaterials.length > 0 &&
    layerNumber !== selectedLayer?.LayerNumber;
  const isCommodityInTargetLayer = (id: number | null) =>
    targetLayerMaterials.some((layer) => layer.CommodityId === id);
  const isOwnLayer = (number: number) =>
    isEditMode && number === selectedLayer.LayerNumber;
  const isFilteringTargetLayer =
    isOwnLayer(layerNumber) || (isPlacementRequired && isAddToExistingLayer);
  const selectableCommodities = isFilteringTargetLayer
    ? enabledCommodities.filter(
        (commodity) =>
          commodity.CommodityId === commodityId ||
          !isCommodityInTargetLayer(commodity.CommodityId),
      )
    : enabledCommodities;

  useEffect(() => {
    if (open) {
      if (selectedLayer) {
        const initialWeight = selectedLayer.Weight || 0;
        setCommodityId(selectedLayer.CommodityId || null);
        const formattedWeight =
          initialWeight === 0 ? "" : Math.round(initialWeight).toString();
        setWeight(formattedWeight);
        setLayerNumber(selectedLayer.LayerNumber || 1);
        setSetAsActive(selectedLayer.IsCurrent || false); // Set based on current layer state
      } else {
        setCommodityId(null);
        setWeight("");
        setLayerNumber(maxLayerNumber + 1);
        setSetAsActive(true); // Default to true for new layers
      }
      setShowCommodityError(false);
      setIsAddToExistingLayer(false);
      setConflictMessage(null);
    }
  }, [open, selectedLayer, maxLayerNumber]);

  const getPlacementMode = (): LayerMoveMode | null => {
    if (!isPlacementRequired) return null;
    return isAddToExistingLayer
      ? LAYER_MOVE_MODE.MOVE_INTO
      : LAYER_MOVE_MODE.REORDER;
  };

  const handleSave = async () => {
    if (!commodityId) {
      setShowCommodityError(true);
      return;
    }

    if (
      getPlacementMode() === LAYER_MOVE_MODE.MOVE_INTO &&
      isCommodityInTargetLayer(commodityId)
    ) {
      setConflictMessage(
        `${selectedCommodity?.CommodityName ?? "This material"} is already in layer ${layerNumber}. It can't be added to it again.`,
      );
      return;
    }

    try {
      // Convert weight string to number before saving
      const numericWeight = parseCraneMagnitude(weight) ?? 0;
      await onSave({
        commodityId,
        weight: numericWeight,
        layerNumber: layerNumber,
        setAsActive: setAsActive,
        placementMode: getPlacementMode(),
      });
      onClose();
    } catch (error) {
      console.error("Error saving charge layer:", error);
    }
  };

  const handleDelete = () => {
    if (!selectedLayer?.ChargeLayerId || !onDelete) return;
    onDelete();
    onClose();
  };

  const handleCommodityChange = (id: number | null) => {
    setCommodityId(id);
    if (id) setShowCommodityError(false);
    setConflictMessage(null);
  };

  const handleAddToExistingLayerChange = (checked: boolean) => {
    setIsAddToExistingLayer(checked);
    setConflictMessage(null);
  };

  const handleWeightChange = (newWeight: string) => {
    setWeight(normalizeCraneMagnitudeInput(newWeight));
  };

  const handleLayerNumberChange = (event: SelectChangeEvent<number>) => {
    const newLayerNumber = event.target.value as number;
    setLayerNumber(newLayerNumber);
    setIsAddToExistingLayer(false);
    setConflictMessage(null);
    if (
      isOwnLayer(newLayerNumber) &&
      otherLayers.some(
        (layer) =>
          layer.LayerNumber === newLayerNumber &&
          layer.CommodityId === commodityId,
      )
    ) {
      setCommodityId(null);
    }
  };

  const selectedCommodity = commodityId
    ? enabledCommodities.find((c) => c.CommodityId === commodityId)
    : null;

  const commodityColor = selectedCommodity?.CommodityColor || "grey.600";

  const isSaveDisabled = isLoading || !commodityId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "95%",
            maxWidth: "1200px",
            borderRadius: "12px",
            minHeight: "600px",
          },
        },
      }}
      maxWidth={false}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          p: 3,
          fontSize: "2rem",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minHeight: "400px",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Grid container spacing={4}>
            {/* Left side: Form fields */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 3,
                      mb: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: "1.6rem", fontWeight: 600 }}>
                      Layer Number
                    </Typography>
                    <FormControlLabel
                      disabled={!isPlacementRequired}
                      control={
                        <Checkbox
                          checked={isPlacementRequired && isAddToExistingLayer}
                          onChange={(e) =>
                            handleAddToExistingLayerChange(e.target.checked)
                          }
                          sx={{ "& .MuiSvgIcon-root": { fontSize: 40 } }}
                        />
                      }
                      label="Add to existing layer"
                      sx={{
                        mr: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "1.4rem",
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Box>
                  <FormControl variant="outlined" fullWidth>
                    <Select
                      value={layerNumber}
                      onChange={handleLayerNumberChange}
                      sx={{
                        bgcolor: "white",
                        borderColor: commodityColor,
                        "& .MuiSelect-select": {
                          fontSize: "1.8rem",
                          padding: "20px",
                          textAlign: "center",
                          fontWeight: 600,
                          minHeight: "30px",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          borderWidth: "2px",
                          height: "70px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "3px",
                          },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: "2px",
                        },
                      }}
                      MenuProps={{
                        slotProps: {
                          paper: {
                            sx: {
                              maxHeight: "400px",
                              overflowY: "auto",
                              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                            },
                          },
                        },
                      }}
                    >
                      {Array.from(
                        { length: maxSelectableLayerNumber },
                        (_, i) => i + 1,
                      ).map((num) => (
                        <MenuItem
                          key={num}
                          value={num}
                          sx={{
                            fontSize: "3rem",
                            fontWeight: 700,
                            padding: "20px 24px",
                            minHeight: "80px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {num}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <CommoditySelectorForOperation
                    commodities={selectableCommodities}
                    value={commodityId}
                    onChange={handleCommodityChange}
                    disabled={false}
                    bgColor="white"
                    borderColor={commodityColor}
                  />
                  {showCommodityError ? (
                    <FormHelperText
                      sx={{ color: "error.main", mt: 1, fontSize: "1.1rem" }}
                    >
                      Commodity selection is required
                    </FormHelperText>
                  ) : null}
                </Box>

                <Box>
                  <WeightInputForOperation
                    value={weight}
                    onChange={handleWeightChange}
                    disabled={false}
                    bgColor="white"
                    borderColor={commodityColor}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 3,
                    bgcolor:
                      "(theme) => theme.palette.charges.backgrounds.light",
                    borderRadius: "12px",
                    border: (theme) =>
                      `3px solid ${theme.palette.charges.borders.medium}`,
                    minHeight: "90px",
                  }}
                >
                  <Typography sx={{ fontSize: "1.8rem", fontWeight: 600 }}>
                    Set as Active Layer
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setAsActive}
                        onChange={(e) => setSetAsActive(e.target.checked)}
                        color="primary"
                        sx={{
                          width: 100,
                          height: 60,
                          padding: 0,
                          "& .MuiSwitch-switchBase": {
                            padding: 1,
                            "&.Mui-checked": {
                              transform: "translateX(40px)",
                              color: "white",
                              "& + .MuiSwitch-track": {
                                bgcolor: "primary.main",
                                opacity: 1,
                              },
                            },
                          },
                          "& .MuiSwitch-thumb": {
                            width: 44,
                            height: 44,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                          },
                          "& .MuiSwitch-track": {
                            borderRadius: 30,
                            height: 60,
                            opacity: 1,
                            bgcolor: (theme) =>
                              theme.palette.charges.status.inactive,
                          },
                        }}
                      />
                    }
                    label=""
                  />
                </Box>
              </Box>
            </Grid>

            {/* Right side: Numeric Keypad */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 3 }}>
                <CustomKeypad
                  value={weight}
                  onChange={handleWeightChange}
                  layout={{
                    default: ["7 8 9", "4 5 6", "1 2 3", "{bksp} 0 {clear}"],
                  }}
                  display={{
                    "{bksp}": "⌫",
                  }}
                  isDecimal={false}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: "24px 32px 32px", width: "100%" }}>
        <Grid container spacing={3}>
          {isEditMode ? (
            <>
              <Grid item xs={4}>
                <Button
                  onClick={handleDelete}
                  disabled={isLoading || !onDelete}
                  variant="contained"
                  sx={{
                    p: "16px 32px",
                    borderRadius: "8px",
                    fontSize: "1.4rem",
                    fontWeight: 600,
                    minHeight: "60px",
                    width: "100%",
                    bgcolor: (theme) => theme.palette.charges.status.warning,
                    color: "white",
                  }}
                >
                  DELETE
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  onClick={onClose}
                  disabled={isLoading}
                  variant="contained"
                  sx={{
                    p: "16px 32px",
                    borderRadius: "8px",
                    fontSize: "1.4rem",
                    fontWeight: 600,
                    minHeight: "60px",
                    width: "100%",
                    bgcolor: (theme) => theme.palette.charges.status.inactive,
                    color: "white",
                  }}
                >
                  CANCEL
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                  variant="contained"
                  sx={{
                    p: "16px 32px",
                    borderRadius: "8px",
                    fontSize: "1.4rem",
                    fontWeight: 600,
                    minHeight: "60px",
                    width: "100%",
                    bgcolor: "primary.main",
                    color: "white",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={32} color="inherit" />
                  ) : (
                    "SAVE"
                  )}
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={6}>
                <Button
                  onClick={onClose}
                  disabled={isLoading}
                  variant="contained"
                  sx={{
                    p: "16px 32px",
                    borderRadius: "8px",
                    fontSize: "1.4rem",
                    fontWeight: 600,
                    minHeight: "60px",
                    width: "100%",
                    bgcolor: (theme) => theme.palette.charges.status.inactive,
                    color: "white",
                  }}
                >
                  CANCEL
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                  variant="contained"
                  sx={{
                    p: "16px 32px",
                    borderRadius: "8px",
                    fontSize: "1.4rem",
                    fontWeight: 600,
                    minHeight: "60px",
                    width: "100%",
                    bgcolor: "primary.main",
                    color: "white",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={32} color="inherit" />
                  ) : (
                    "SAVE"
                  )}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </DialogActions>

      <ErrorDetailsModal
        open={conflictMessage !== null}
        title="Material already in layer"
        message={conflictMessage ?? ""}
        onClose={() => setConflictMessage(null)}
      />
    </Dialog>
  );
};
